<template>
  <ion-page>
    <ion-content class="h-full">
      <round-header
        image-type="emoji"
        image="😊"
        :image-class="`transition-all duration-1000`"
        title="Mood Tracker"
        subtitle="Toolbox"
        back="/tools"
        disable-edit-mode="true"
      >
      </round-header>
      <div class="flex justify-center">
        <div class="w-full max-w-[1024px] p-4 px-8 md:px-20">
          <!-- Error message -->
          <div v-if="error" class="mb-4 rounded-lg bg-red-100 p-4 text-red-700">
            {{ error }}
          </div>

          <!-- Current Mood Section -->
          <div class="mb-8 space-y-4 text-center">
            <h2 class="font-fredoka font-outline-1 text-2xl text-white">
              How are you feeling today?
            </h2>

            <!-- Mood Selection -->
            <div class="grid grid-cols-5 gap-4">
              <button
                v-for="mood in moodOptions"
                :key="mood"
                class="flex h-24 cursor-pointer flex-col items-center rounded-xl border-2 p-4 transition-all duration-200"
                :class="[
                  currentMood === mood
                    ? 'border-white bg-[#ffffff55]'
                    : 'border-[#ffffff33] bg-[#ffffff22] hover:bg-[#ffffff33]',
                ]"
                @click="() => selectMood(mood)"
              >
                <span
                  class="text-3xl transition-all duration-200"
                  :class="currentMood === mood ? 'scale-150' : 'scale-100'"
                  >{{ Mood.getMoodEmoji(mood) }}</span
                >
                <span
                  class="mt-2 text-sm text-gray-500 transition-all duration-200"
                  :class="currentMood === mood ? 'scale-150 font-bold' : 'scale-100'"
                  >{{ Mood.getMoodText(mood) }}</span
                >
              </button>
            </div>

            <!-- Note Input -->
            <div class="mt-4">
              <textarea
                v-model="currentNote"
                placeholder="Add a note about how you're feeling (optional)"
                class="font-quicksand w-full resize-none rounded-xl border border-[#d8b99b] bg-[#fff7ef] p-4 text-base text-[#5c3d2e] shadow-inner focus:ring-2 focus:ring-[#caa37b] focus:outline-none"
                rows="3"
              ></textarea>
            </div>

            <!-- Save Button -->
            <button
              :disabled="isLoading"
              class="bg-fill-positive hover:bg-bg-positive mt-4 cursor-pointer rounded-xl px-6 py-2 text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
              @click="saveMood"
            >
              {{ isLoading ? 'Saving...' : 'Save Mood' }}
            </button>
          </div>

          <!-- Mood History Section -->
          <div v-if="isLoading && moods.length === 0" class="py-8 text-center">
            <div
              class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            >
              <span
                class="!absolute !-m-px !h-px !w-px !overflow-hidden !border-0 !p-0 !whitespace-nowrap ![clip:rect(0,0,0,0)]"
                >Loading...</span
              >
            </div>
            <p class="mt-2 text-gray-500">Loading your mood history...</p>
          </div>

          <div v-else-if="moods.length > 0" class="space-y-4">
            <h3 class="font-fredoka font-outline-1 text-center text-3xl text-white">
              Your Mood History
            </h3>

            <!-- Mood History List -->
            <div class="space-y-3">
              <div v-for="mood in moods" :key="mood.id" class="rounded-xl bg-[#ffffff44] p-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <span class="mr-3 text-2xl">{{ Mood.getMoodEmoji(mood.value) }}</span>
                    <div>
                      <div class="font-semibold text-gray-800">
                        {{ Mood.getMoodText(mood.value) }}
                      </div>
                      <div class="text-sm text-gray-600">
                        {{ new Date(mood.createdAt).toLocaleString() }}
                      </div>
                    </div>
                  </div>
                  <button
                    :disabled="isLoading"
                    class="text-gray-500 transition-colors hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Delete mood entry"
                    @click="() => deleteMood(mood.id)"
                  >
                    <Trash2 class="h-5 w-5" />
                  </button>
                </div>
                <div v-if="mood.note" class="mt-2 text-gray-700">
                  {{ mood.note }}
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!isLoading" class="mt-8 text-center text-gray-500">
            <div class="mb-2 text-4xl">📝</div>
            <p>No mood entries yet. Start tracking your mood today!</p>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IonContent, IonPage } from '@ionic/vue';
import RoundHeader from '../../components/RoundHeader.vue';
import { Trash2 } from 'lucide-vue-next';
import { Mood, type MoodValue } from '../../models/Mood.ts';
import { collection, doc, setDoc, deleteDoc, getFirestore } from 'firebase/firestore';
import { useCurrentUser, useCollection } from 'vuefire';

// Firebase setup
const db = getFirestore();
const user = useCurrentUser();

// Available mood options
const moodOptions: MoodValue[] = ['great', 'good', 'neutral', 'bad', 'awful'];

// Current mood selection
const currentMood = ref<MoodValue>('neutral');
const currentNote = ref('');

// Stored moods
const error = ref<string | null>(null);

// Use VueFire's useCollection to reactively fetch moods
const moodsCollection = useCollection(() =>
  user.value ? collection(db, `users/${user.value.uid}/moods`) : null
);

// Transform the raw Firestore data into Mood objects and sort by creation date
const moods = computed(() => {
  if (!moodsCollection.value) return [];

  return moodsCollection.value
    .map((data) => {
      const mood = new Mood();
      Object.assign(mood, data);
      return mood;
    })
    .sort((a, b) => b.createdAt - a.createdAt);
});

// Loading state is handled by VueFire
const isLoading = computed(() => !user.value || moodsCollection.value === undefined);

// Select a mood
function selectMood(mood: MoodValue) {
  currentMood.value = mood;
}

// Save the current mood
async function saveMood() {
  if (!user.value) {
    error.value = 'You must be logged in to save moods';
    return;
  }

  error.value = null;
  // No need to set isLoading as it's now a computed property

  try {
    const mood = new Mood();
    mood.value = currentMood.value;
    mood.note = currentNote.value;

    const moodRef = doc(db, `users/${user.value.uid}/moods/${mood.id}`);
    await setDoc(moodRef, mood.toPlain());

    // Reset form
    currentMood.value = 'neutral';
    currentNote.value = '';

    // No need to refresh moods list as VueFire will handle this automatically
  } catch (err) {
    console.error('Error saving mood:', err);
    error.value = 'Failed to save mood. Please try again.';
  }
}

// Delete a mood entry
async function deleteMood(id: string) {
  if (!user.value) {
    error.value = 'You must be logged in to delete moods';
    return;
  }

  error.value = null;
  // No need to set isLoading as it's now a computed property

  try {
    const moodRef = doc(db, `users/${user.value.uid}/moods/${id}`);
    await deleteDoc(moodRef);

    // No need to refresh moods list as VueFire will handle this automatically
  } catch (err) {
    console.error('Error deleting mood:', err);
    error.value = 'Failed to delete mood. Please try again.';
  }
}

// No need for onMounted or onIonViewWillEnter hooks as VueFire automatically fetches data
// when the component is mounted and keeps it in sync
</script>
